// ============================================================================
// OBT F2-B — Park bazlı dinamik cevap formu (owner-detail hydration)
// ============================================================================
// Form soru TANIMI yalnız publication snapshot'tan; cevap İÇERİĞİ owner detail
// (`GET /responses/{id}/my-detail`) ile geri yüklenir. Taslak/gönderilmiş/
// revizyon cevap yeniden açıldığında cevaplar, seçimler ve fotoğraflar görünür.
//
// Fotoğraf DOĞRU soruya (answer_id) bağlanır: hook önce answer'ı oluşturur,
// detail'i tazeler, answer_id ile upload eder. `photo_required` tamamlanma
// answer-bağlı fotoğrafa göre değerlendirilir (backend son otorite).
//
// İyimser kilit: PATCH `expected_version` gönderir; 409'da güncel taslak
// yeniden yüklenir (kaydedilmemiş değişiklikler kaybolur; kullanıcı uyarılır).
// ============================================================================

import { useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  Send,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Camera,
} from 'lucide-react';
import { ObtErrorState, ObtStatusBadge, ObtLoading } from '../ObtStates';
import { RESPONSE_STATUS_LABELS } from '../obtEnums';
import DynamicAnswerField from './DynamicAnswerField';
import PhotoThumb from './PhotoThumb';
import { useParkResponse } from './useParkResponse';
import { validateForSubmit, validatePhotoFile } from './responseValidation';
import {
  RESPONSE_STATUS,
  isLockedStatus,
  isPhotoQuestion,
  responseStatusLabel,
} from './responseEnums';

function formatDateTime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' });
}

/** Bir sorunun (answer'a bağlı) fotoğrafları + yükleme kontrolü. */
function QuestionPhotos({ responseId, photoIds, readOnly, onPick, uploading, inputLabel = 'Bu soruya fotoğraf ekle' }) {
  const fileRef = useRef(null);
  const ids = Array.isArray(photoIds) ? photoIds : [];
  return (
    <div className="mt-2 space-y-2 rounded-md border border-dashed border-gray-200 p-2">
      <p className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
        <Camera className="h-3.5 w-3.5" aria-hidden="true" />
        Fotoğraflar
      </p>
      {ids.length > 0 && responseId && (
        <div className="flex flex-wrap gap-2">
          {ids.map((pid) => (
            <PhotoThumb key={pid} responseId={responseId} photoId={pid} />
          ))}
        </div>
      )}
      {!readOnly && (
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          aria-label={inputLabel}
          className="block text-xs"
          disabled={uploading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (fileRef.current) fileRef.current.value = '';
            if (file) onPick(file);
          }}
        />
      )}
      {readOnly && ids.length === 0 && (
        <p className="text-xs text-gray-400">Fotoğraf yok.</p>
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {string} props.publicationId
 * @param {object} props.park
 * @param {Array}  props.questions
 * @param {() => void} props.onBack
 * @param {() => void} props.onSaved
 */
export default function ParkResponseForm({
  publicationId,
  dueAt,
  duePassed: duePassedFlag,
  park,
  questions,
  onBack,
  onSaved,
}) {
  const r = useParkResponse({ publicationId, park, questions });
  const [confirm, setConfirm] = useState(null); // 'submit' | 'resubmit' | null
  const [clientErrors, setClientErrors] = useState([]);
  // Yeniden gönderirken İNCELEYENE mesaj (BM'nin revizyon isteğine yanıt).
  const [resubmitMessage, setResubmitMessage] = useState('');

  const locked = isLockedStatus(r.status);
  const isRevision = r.status === RESPONSE_STATUS.REVISION_REQUESTED;
  // Süre geçtiyse taslak yine açılıp KAYDEDİLEBİLİR (kurumsal hafıza) ama resmi
  // gönderim/yeniden gönderim KAPALIDIR — sunucu da reddeder; UI'yı buna hizala.
  // Önce SUNUCUNUN taze `due_passed` bayrağına güven (doğru TZ + sunucu saati;
  // liste öğesi bayat olabilir — süre sonradan uzatılmışsa). Bayrak yoksa ya da
  // form açıkken süre dolarsa istemci-tarafı taze `dueAt` ile de kontrol et.
  const clientDuePassed =
    dueAt != null && new Date(dueAt).getTime() < Date.now();
  const duePassed =
    (typeof duePassedFlag === 'boolean' ? duePassedFlag : false) ||
    clientDuePassed;

  // photo/photo_required tamamlanma: answer'a bağlı gerçek fotoğraf sayısından.
  const photoCountByQuestion = useMemo(() => {
    const map = {};
    (questions || []).forEach((q) => {
      map[q.question_id] = (r.photoIdsByQuestion[q.question_id] || []).length;
    });
    return map;
  }, [questions, r.photoIdsByQuestion]);

  // answer'a bağlı OLMAYAN medya (response düzeyi) — "Diğer fotoğraflar".
  const orphanMedia = useMemo(
    () => (r.media || []).filter((mmm) => !mmm.answer_id),
    [r.media]
  );

  const handleSaveDraft = async () => {
    setClientErrors([]);
    const res = await r.saveDraft();
    if (res?.ok) {
      toast.success('Taslak kaydedildi.');
      onSaved?.();
      // Model A: kaydet → park listesine dön (sıradaki parkı doldur). Gönderim,
      // listedeki "Önizle ve Gönder" ile TOPLU yapılır; burada kilit YOK.
      onBack?.();
    }
  };

  const handlePickPhoto = async (file, questionId) => {
    const check = validatePhotoFile(file);
    if (!check.ok) {
      toast.error(check.message);
      return;
    }
    const res = await r.uploadPhoto(file, questionId);
    if (res?.ok) {
      toast.success('Fotoğraf yüklendi.');
      onSaved?.();
    } else if (res?.error) {
      toast.error(res.error.message);
    }
  };

  const openSubmitConfirm = () => {
    const { ok, errors } = validateForSubmit(questions, r.answers, photoCountByQuestion);
    if (!ok) {
      setClientErrors(errors);
      toast.error('Göndermeden önce eksikleri giderin.');
      return;
    }
    setClientErrors([]);
    setConfirm(isRevision ? 'resubmit' : 'submit');
  };

  const doSubmit = async () => {
    const action = confirm;
    setConfirm(null);
    const res =
      action === 'resubmit'
        ? await r.resubmit(resubmitMessage)
        : await r.submit();
    if (res?.ok) {
      toast.success(action === 'resubmit' ? 'Cevap yeniden gönderildi.' : 'Cevap gönderildi.');
      setResubmitMessage('');
      onSaved?.();
      // Gönderim sonrası kilitli tek-park ekranında kalma; park listesine dön.
      onBack?.();
    }
  };

  // photo/photo_required olan sorularda foto kontrolü göster.
  const needsPhotos = (q) => isPhotoQuestion(q.question_type) || q.photo_required;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack} disabled={r.submitting}>
          <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" />
          Parklar
        </Button>
        {r.status && <ObtStatusBadge value={r.status} labelMap={RESPONSE_STATUS_LABELS} />}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{park.park_name}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-xs text-gray-600">
          <p>{park.region_name || 'Bölge belirtilmemiş'}</p>
          {r.status && (
            <p>
              Durum: {responseStatusLabel(r.status)}
              {park.submitted_at ? ` · Gönderim: ${formatDateTime(park.submitted_at)}` : ''}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Çakışma uyarısı — güncel taslağı yeniden yükle */}
      {r.conflict && (
        <div
          role="alert"
          className="flex flex-col gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800 ring-1 ring-inset ring-red-200 sm:flex-row sm:items-center sm:justify-between"
        >
          <span className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            Bu cevap başka bir işlemle güncellendi. Güncel taslağı yeniden
            yükleyin (kaydedilmemiş değişiklikleriniz kaybolur).
          </span>
          <Button variant="outline" size="sm" onClick={r.reloadDetail} disabled={r.submitting}>
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
            Güncel taslağı yükle
          </Button>
        </div>
      )}

      {/* Revizyon bilgisi (owner detail'den) */}
      {isRevision && r.revision && (
        <div
          role="note"
          className="space-y-1 rounded-lg bg-amber-50 p-3 text-xs text-amber-900 ring-1 ring-inset ring-amber-200"
        >
          <p className="font-semibold">
            Revizyon istendi
            {r.revision.revision_number ? ` (#${r.revision.revision_number})` : ''}
          </p>
          {r.revision.comment && <p>Not: {r.revision.comment}</p>}
          <p className="text-amber-700">
            {r.revision.requested_by?.name
              ? `İsteyen: ${r.revision.requested_by.name}`
              : ''}
            {r.revision.requested_at
              ? ` · ${formatDateTime(r.revision.requested_at)}`
              : ''}
          </p>
          <p>Gerekli düzeltmeleri yapıp yeniden gönderin.</p>
        </div>
      )}

      {r.detailLoading ? (
        <ObtLoading label="Cevap ayrıntısı yükleniyor…" />
      ) : (
        <>
          {locked && (
            <div
              role="note"
              className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600 ring-1 ring-inset ring-gray-200"
            >
              Bu cevap gönderildi ve kilitlendi; salt okunur görüntüleniyor.
            </div>
          )}

          {/* Dinamik sorular (salt okunur veya düzenlenebilir) */}
          <div className="space-y-3">
            {questions.map((q) => (
              <div key={q.question_id}>
                <DynamicAnswerField
                  question={q}
                  answer={r.answers[q.question_id] || {}}
                  onChange={(patch) => r.setAnswer(q.question_id, patch)}
                  readOnly={locked}
                />
                {needsPhotos(q) && (
                  <QuestionPhotos
                    responseId={r.responseId}
                    photoIds={r.photoIdsByQuestion[q.question_id]}
                    readOnly={locked}
                    uploading={r.submitting}
                    onPick={(file) => handlePickPhoto(file, q.question_id)}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Genel cevap notu */}
          <div className="space-y-1.5">
            <Label htmlFor="response-text">Genel not (isteğe bağlı)</Label>
            <Textarea
              id="response-text"
              value={r.responseText}
              maxLength={8000}
              disabled={locked}
              placeholder="Bilgi talebi geneline dair not"
              onChange={(e) => r.setResponseText(e.target.value)}
            />
          </div>

          {/* Diğer fotoğraflar (belirli bir soruya bağlı OLMAYAN, genel).
              DÜZENLENEBİLİR durumda HER ZAMAN göster + EKLE butonu: böylece soru
              fotoğraf tipi olmasa da (ör. Evet/Hayır) ve BM revizyon'da "fotoğraf
              ekle" dediğinde Formen buraya genel fotoğraf ekleyebilir. */}
          {(!locked || orphanMedia.length > 0) && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Fotoğraflar (genel)</CardTitle>
                {!locked && (
                  <p className="text-xs text-gray-500">
                    Bir soruya bağlı olmayan genel fotoğraf(lar) ekleyebilirsiniz.
                  </p>
                )}
              </CardHeader>
              <CardContent>
                <QuestionPhotos
                  responseId={r.responseId}
                  photoIds={orphanMedia.map((mmm) => mmm.photo_id)}
                  readOnly={locked}
                  uploading={r.submitting}
                  onPick={(file) => handlePickPhoto(file, null)}
                  inputLabel="Genel fotoğraf ekle"
                />
              </CardContent>
            </Card>
          )}

          {/* İstemci hata özeti */}
          {clientErrors.length > 0 && (
            <div
              role="alert"
              className="rounded-lg bg-red-50 p-4 text-sm text-red-800 ring-1 ring-inset ring-red-200"
            >
              <p className="mb-1 font-semibold">Gönderim için eksikler:</p>
              <ul className="list-inside list-disc space-y-0.5">
                {clientErrors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          {r.error && !r.conflict && <ObtErrorState error={r.error} />}

          {/* Aksiyon çubuğu — yalnız düzenlenebilir durumda */}
          {!locked && (
            <div className="sticky bottom-0 flex flex-col gap-2 border-t border-gray-200 bg-white/95 py-3 backdrop-blur sm:flex-row sm:justify-end">
              {duePassed && (
                <p role="note" className="self-center text-xs text-amber-700 sm:mr-auto">
                  Cevap süresi doldu. Taslak kaydedilebilir, ancak resmi olarak gönderilemez.
                </p>
              )}
              {!isRevision && (
                <p className="self-center text-xs text-gray-500 sm:mr-auto">
                  Kaydettikten sonra tüm parkları <strong>“Önizle ve Gönder”</strong>
                  ile birlikte gönderirsiniz.
                </p>
              )}
              <Button onClick={handleSaveDraft} disabled={r.submitting}>
                {r.submitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Save className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                {isRevision ? 'Taslağı Kaydet' : 'Kaydet'}
              </Button>
              {/* Per-park anlık gönderim yalnız REVİZYON'da (BM geri gönderdi):
                  Model A'da normal taslaklar toplu gönderilir, tek tek DEĞİL. */}
              {isRevision && !duePassed && (
                <Button onClick={openSubmitConfirm} disabled={r.submitting}>
                  {r.submitting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" aria-hidden="true" />
                  )}
                  Yeniden Gönder
                </Button>
              )}
            </div>
          )}
        </>
      )}

      {/* Gönderim onayı */}
      <AlertDialog open={confirm != null} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === 'resubmit' ? 'Yeniden gönder' : 'Cevabı gönder'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === 'resubmit'
                ? 'Düzeltilen cevap yeniden incelemeye gönderilecektir.'
                : 'Bu park için verdiğiniz cevap incelemeye gönderilecektir. Gönderim sonrasında cevap kilitlenecektir.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirm === 'resubmit' && (
            <div className="space-y-1.5">
              <Label htmlFor="resubmit-msg">
                İnceleyene mesaj (isteğe bağlı)
              </Label>
              <Textarea
                id="resubmit-msg"
                value={resubmitMessage}
                maxLength={1000}
                placeholder="BM'nin isteğine yanıtınız — ör. neden fotoğraf ekleyemediniz"
                onChange={(e) => setResubmitMessage(e.target.value)}
              />
              <p className="text-xs text-gray-500">
                Bu mesaj inceleme geçmişinde “Formen yeniden gönderdi” altında görünür.
              </p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={r.submitting}>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={doSubmit} disabled={r.submitting}>
              {confirm === 'resubmit' ? 'Yeniden Gönder' : 'Gönder'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
